<template>
  <section class="filter-and-sort-facets">
    <ui-collapse
      v-for="group of visibleGroups"
      :id="`facet-group-${group.slug}`"
      :key="group.slug"
      :selected="$isDesktop ? true : group.showByDefault"
      :data-test="`facet-groups-${group.slug}`"
      class="facet-group"
      :collapse-id="`collapse-facet-group-${group.slug}`"
    >
      <template #ui-collapse-title>
        {{ group.name }}
      </template>
      <template #ui-collapse-subtitle>
        <translate
          class="selected-item-count"
          :class="{ active: selectedPerGroup[group.slug] > 0 }"
          :tt="{ count: selectedPerGroup[group.slug] }"
        >
          {count} selected
        </translate>
      </template>
      <template #ui-collapse-body>
        <filter-and-sort-facets-group-ranked
          v-if="group.type === 'ranked'"
          :group="group"
          :counts="facetCounts"
          :initial-facet-counts="initialFacetCounts"
          :selected-facets="selectedFacets"
          @facets-change="facetsChange"
        />
        <filter-and-sort-facets-group-default
          v-else
          :group="group"
          :counts="facetCounts"
          :initial-facet-counts="initialFacetCounts"
          :selected-facets="selectedFacets"
          @facets-change="facetsChange"
        />
      </template>
    </ui-collapse>
  </section>
</template>

<script lang="ts">
import { getCampsiteCategoriesPresenter } from '~/apps/categories/presenter-json/CampsiteCategoriesUpdaterJson'
import type { FacetCounts } from '~/apps/facets/domain/FacetCounts'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import { logger } from '../../../../../utility/logger'

type FacetsChanges = Array<[string, boolean]>

@Component({
  setup() {
    const facetsStore = useFacetsStoreSetup(usePinia())
    const searchWizardStore = useSearchWizardStore(usePinia())
    return {
      facetsStore,
      searchWizardStore,
    }
  },
  emits: ['changeFacets'],
})
class FilterAndSortFacets extends Vue {
  facetsStore: ReturnType<typeof useFacetsStoreSetup>
  searchWizardStore: ReturnType<typeof useSearchWizardStore>

  @Prop({ default: () => [] })
  selectedFacets: string[]

  @Prop()
  facetCounts: FacetCounts

  get lang() {
    const route = this.$route
    return (route.params.lang as string) || 'en-gb'
  }

  get subcategoryFacetSlugs() {
    const presenter = getCampsiteCategoriesPresenter(this.lang)
    return presenter.subcategoryFacetSlugs || []
  }

  get facetGroups() {
    return this.facetsStore.facetGroups || []
  }

  @Prop({ required: false })
  removedFacet: string

  @Prop()
  initialFacetCounts: FacetCounts

  selectedPerGroup = {}

  facetsChange(changes: FacetsChanges) {
    logger('FilterAnSortFacets.facetsChange', changes)
    this.$emit('changeFacets', this.recalculateSelectedFacets(changes))
  }

  get visibleGroups() {
    return this.facetGroups
      .map((group) => ({
        ...group,
        facets: group.hideEmpty
          ? group.facets.filter(
              (facet) =>
                // if we are not showing counts then show all
                !this.searchWizardStore.showCount ||
                // otherwise only show the facets that match initial counts
                this.initialFacetCounts[facet.slug] ||
                this.facetCounts[facet.slug],
            )
          : group.facets,
      }))
      .filter(
        (group) => group.facets.length > 0 && group.slug !== 'campsite-group',
      )
  }

  private recalculateSelectedFacets(changes: FacetsChanges) {
    const facetsRemoved: string[] = []
    const tmpFacets = [...this.selectedFacets]

    for (const [slug, isAdded] of changes) {
      // If a subcategory facet is being added, remove any existing selected subcategory facets
      if (isAdded && this.subcategoryFacetSlugs.includes(slug)) {
        // Find any currently selected subcategory facets and add them to facetsRemoved
        for (const existingFacet of tmpFacets) {
          if (this.subcategoryFacetSlugs.includes(existingFacet)) {
            facetsRemoved.push(existingFacet)
            // Update the count for the facet that's being removed
            this.updateSelectedCounts(existingFacet, false)
          }
        }
      }

      isAdded ? tmpFacets.push(slug) : facetsRemoved.push(slug)
      this.updateSelectedCounts(slug, isAdded)
    }

    return tmpFacets.filter((slug) => !facetsRemoved.includes(slug))
  }

  @Watch('selectedFacets', { immediate: true })
  selectedFacetsChanged() {
    this.setupGroupCounts()
  }

  setupGroupCounts() {
    const selectedPerGroup = {}
    for (const group of this.visibleGroups) {
      selectedPerGroup[group.slug] = 0
    }
    this.selectedPerGroup = selectedPerGroup
    for (const facet of this.selectedFacets) {
      this.updateSelectedCounts(facet, true)
    }
  }

  updateSelectedCounts(selectedFacet, checked) {
    const parentGroupsForSelectedFacet = this.facetGroups
      .filter((slug) =>
        slug.facets.some((facet) => facet.slug === selectedFacet),
      )
      .map((facetGroups) => facetGroups.slug)
    for (const parentGroup of parentGroupsForSelectedFacet) {
      if (checked) {
        this.selectedPerGroup[parentGroup]++
      } else {
        this.selectedPerGroup[parentGroup]--
      }
    }
  }
}

export default toNative(FilterAndSortFacets)
</script>

<style scoped>
.filter-and-sort-facets :deep(.count) {
  margin-left: auto;
}

.filter-and-sort-facets :deep(.facets-group) {
  padding: 1em;
}

.filter-and-sort-facets :deep(.ui-collapse-header .ui-collapse-title) {
  font-weight: bold;
}

.filter-and-sort-facets :deep(.ui-collapse-subtitle) {
  flex-shrink: 0;
}

.filter-and-sort-facets :deep(.temporary-hide) {
  display: none !important;
}

.selected-item-count {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  background: var(--colour-light-yellow);
  border: 1em;
  padding: 0.2em 0.5em;
  border-radius: 1em;
  font-size: var(--small);
  transition:
    transform 80ms ease-out,
    opacity 120ms ease-out;
  transform: scale(0.75);
  opacity: 0;
}

.selected-item-count.active {
  opacity: 1;
  transform: scale(1);
}
</style>
